import type { Drug } from '../components/drug-search';
import type { InteractionCheckResult } from '../types';

// ============================================================================
// Types
// ============================================================================

export type PairStatus = 'pending' | 'processing' | 'success' | 'failed';

export interface InteractionApiResult {
  drug1Id: string;
  drug2Id: string;
  hasInteraction: boolean;
  severity?: 'critical' | 'important' | 'moderate' | 'minor';
  summary?: string;
  sources: Array<{
    drugId: string;
    drugName?: string;
    activeSubstance?: string;
    texts: string[];
    chapter45Anchors?: Array<{ blockId: string; start: number; end: number }>;
  }>;
  fromCache: boolean;
}

export interface InteractionPairStatus {
  drug1Id: string;
  drug2Id: string;
  status: PairStatus;
  result?: InteractionApiResult;
}

export interface CheckInteractionsResponse {
  pairs: InteractionPairStatus[];
  stats: {
    pending: number;
    processing: number;
    success: number;
    failed: number;
  };
  allDone: boolean;
}

/** Realtime row shape from `interaction_cache` table */
export interface InteractionCacheRow {
  drug1_id: string;
  drug2_id: string;
  ma_interakcja: boolean | null;
  istotnosc: string | null;
  result_json: {
    summary?: string;
    sources?: Array<{
      drugId: string;
      displayTexts?: string[];
      anchors?: Array<{ blockId: string; start: number; end: number }>;
    }>;
  } | null;
  processing_status: string;
}

// ============================================================================
// Pure helpers
// ============================================================================

/** Canonical pair key — always `smallerId:largerId` */
export function getPairKey(id1: string, id2: string): string {
  return id1 < id2 ? `${id1}:${id2}` : `${id2}:${id1}`;
}

/** Map Polish severity label to English */
function mapSeverityToEnglish(
  severity: string,
): 'critical' | 'important' | 'moderate' | 'minor' | undefined {
  switch (severity) {
    case 'krytyczna':
      return 'critical';
    case 'istotna':
      return 'important';
    case 'umiarkowana':
      return 'moderate';
    case 'niewielka':
      return 'minor';
    default:
      return undefined;
  }
}

/** Convert a Realtime `interaction_cache` row to the API result shape */
export function cacheRowToApiResult(
  cache: InteractionCacheRow,
): InteractionApiResult {
  const resultJson = cache.result_json;

  return {
    drug1Id: cache.drug1_id,
    drug2Id: cache.drug2_id,
    hasInteraction: cache.ma_interakcja ?? false,
    severity: cache.istotnosc
      ? mapSeverityToEnglish(cache.istotnosc)
      : undefined,
    summary: resultJson?.summary,
    sources: (resultJson?.sources ?? [])
      .filter((q) => Array.isArray(q.displayTexts) && q.displayTexts.length > 0)
      .map((q) => ({
        drugId: q.drugId,
        texts: q.displayTexts ?? [],
        chapter45Anchors: q.anchors,
      })),
    fromCache: true,
  };
}

/** Map an API result to the frontend `InteractionCheckResult` shape */
export function mapApiResultToFrontend(
  result: InteractionApiResult,
  drugsMap: Map<string, Drug>,
): InteractionCheckResult {
  const drug1 = drugsMap.get(result.drug1Id);
  const drug2 = drugsMap.get(result.drug2Id);

  if (!drug1 || !drug2) {
    throw new Error(`Drug not found: ${result.drug1Id} or ${result.drug2Id}`);
  }

  if (!result.hasInteraction) {
    return {
      pair: { drug1, drug2 },
      hasInteraction: false,
    };
  }

  return {
    pair: { drug1, drug2 },
    hasInteraction: true,
    severity: result.severity!,
    summary: result.summary ?? '',
    chplQuotes: result.sources.map((q) => {
      const drug = drugsMap.get(q.drugId);
      return {
        drugId: q.drugId,
        chplId: drug?.chplId,
        drugName: q.drugName ?? drug?.productName ?? q.drugId,
        activeSubstance: q.activeSubstance ?? drug?.commonName,
        strength: drug?.strength,
        form: drug?.form,
        texts: q.texts,
        chapter45Anchors: q.chapter45Anchors,
        url: q.drugId ? `/api/chpl/${q.drugId}` : undefined,
      };
    }),
  };
}

// ============================================================================
// Result merging (single source of truth — previously duplicated 4×)
// ============================================================================

/**
 * Merge new results into the results map, removing them from pending pairs.
 *
 * @returns `true` if any new results were actually added.
 */
export function mergeResults(
  newResults: InteractionCheckResult[],
  resultsMap: Map<string, InteractionCheckResult>,
  pendingPairs: Set<string>,
): boolean {
  let added = false;

  for (const result of newResults) {
    const key = getPairKey(result.pair.drug1.id, result.pair.drug2.id);

    if (!resultsMap.has(key)) {
      resultsMap.set(key, result);
      pendingPairs.delete(key);
      added = true;
    }
  }

  return added;
}
